// R2 endpoint for single uploads
export const getR2PresignedUrlEndpoint = '/uploads/presigned-url/r2';

// R2 Multipart Upload endpoints
export const r2MultipartInitiateEndpoint = '/uploads/r2/multipart/initiate';
export const r2MultipartPresignedUrlsEndpoint =
  '/uploads/r2/multipart/presigned-urls';
export const r2MultipartCompleteEndpoint = '/uploads/r2/multipart/complete';
export const r2MultipartAbortEndpoint = (uploadId, objectKey) =>
  `/uploads/r2/multipart/${uploadId}?objectKey=${encodeURIComponent(
    objectKey
  )}`;

/**
 * GET Returns a cursor-paginated CMS audit timeline for one resource.
 * @returns {`/cmsAuditTrail/resourceTimeline`}
 */
export const cmsAuditResourceTimelineEndpoint =
  '/cmsAuditTrail/resourceTimeline';

/** ############################# **
 *  ##### ACTIONS ENDPOINTS #####
 *  ############################# */

/**
 * GET Returns All Active Actions
 * @returns {`/actionLinks/active`}
 */
export const activeActionsEndpoint = `/actionLinks/active`;

/**
 * GET/PUT Action
 * @returns {`/actionLinks/{string}`}
 */
export const singleActionEndpoint = actionId => `/actionLinks/${actionId}`;

/**
 * GET How many Stories, Clips and Cards use an Action
 * @returns {`/actionLinks/${string}/usage`}
 */
export const actionUsageEndpoint = actionId => `/actionLinks/${actionId}/usage`;

/**
 * GET One page of the Stories, Clips or Cards that use an Action
 * @param {string} actionId
 * @param {'stories' | 'clips' | 'cards'} usageType
 * @returns {`/actionLinks/${string}/usage/${string}`}
 */
export const actionUsageItemsEndpoint = (actionId, usageType) =>
  `/actionLinks/${actionId}/usage/${usageType}`;

/**
 * POST Creates Action
 * @returns {`/actionLinks`}
 */
export const saveActionEndpoint = `/actionLinks`;

/**
 * PATCH Deactivates Action
 * @returns {`/actionLinks/${string}/deactivate`}
 */
export const deactivateActionEndpoint = actionId =>
  `/actionLinks/${actionId}/deactivate`;

/**
 * GET Checks if Actions Title is Available
 * @returns {`/actionLinks/titleIsAvailable`}
 */
export const isTitleAvailableEndpoint = `/actionLinks/titleIsAvailable`;

/**
 * GET Action Types
 * @returns {`/actionLinks/types`}
 */
export const actionTypesEndpoint = `/actionLinks/types`;

/**
 * GET Menu Items
 * @returns {`system/menu`}
 */
export const menuEndpoint = `system/menu`;

/** ###################################### **
 *  ##### ENGAGEMENT UNITS ENDPOINTS #####
 *  ###################################### */

/**
 * POST Poll
 * @type {string}
 */
export const postPollEndpoint = '/poll';

/**
 * GET/PUT/DELETE Poll by Id
 */
export const pollByIdEndpoint = pollId => `/poll/${pollId}`;

/**
 * POST Duplicate Poll
 * @returns {`/poll/${string}/duplicate`}
 */
export const duplicatePollEndpoint = pollId => `/poll/${pollId}/duplicate`;

/**
 * POST Answers for poll
 */
export const answersForPollEndpoint = pollId => `/poll/${pollId}/answers`;

/**
 * POST Trivia Quiz
 * @type {string}
 */
export const postTriviaQuizEndpoint = '/triviaQuiz';

/**
 * GET/DELETE Trivia Quiz by Id
 */
export const triviaQuizEndpointById = id => `/triviaQuiz/${id}`;

/**
 * POST Duplicate Trivia Quiz
 * @returns {`/triviaQuiz/${string}/duplicate`}
 */
export const duplicateTriviaQuizEndpoint = triviaQuizId =>
  `/triviaQuiz/${triviaQuizId}/duplicate`;

/**
 * POST Trivia Quiz questions
 */
export const triviaQuizQuestionEndpoint = id => `/triviaQuiz/${id}/question`;

/**
 * DELETE Trivia Quiz question
 */
export const triviaQuizQuestionByIdEndpoint = (id, questionId) =>
  `/triviaQuiz/${id}/question/${questionId}`;

/**
 * PUT Trivia Quiz questions order
 */
export const triviaQuizQuestionsOrderEndpoint = id =>
  `/triviaQuiz/${id}/questionsOrder`;

/**
 * PUT Trivia Quiz results page
 */
export const triviaQuizResultPageEndpoint = id =>
  `/triviaQuiz/${id}/resultPage`;

/**
 * GET Engagement Unit by Id
 */
export const engagementUnitByIdEndpoint = pollId => `/engagementUnit/${pollId}`;

/**
 * GET Engagement Units
 * @returns {`/engagementUnit`}
 */
export const engagementUnitsEndpoint = '/engagementUnit';

/**
 * POST Creates Engagement Unit page in story
 * @returns {`/page/engagementUnitPage`}
 */
export const engagementUnitPageEndpoint = '/page/engagementUnitPage';

/**
 * GET Trivia Quiz by Id
 */
export const triviaQuizByIdEndpoint = triviaQuizId =>
  `/triviaQuiz/${triviaQuizId}`;

/**
 * GET EngagementUnit
 * @returns {`/engagementUnit/{string}`}
 */
export const engagementUnitById = engagementUnitId =>
  `/engagementUnit/${engagementUnitId}`;

/**
 * POST Add Poll to story (by Poll ID)
 * @returns {string}
 */
export const addPollToStoryEndpoint = `/poll/pollToStory`;

/**
 * POST Add Quiz to story (by Quiz ID)
 * @returns {string}
 */
export const addQuizToStoryEndpoint = `/triviaQuiz/quizToStory`;

/**
 * POST Add Engagement Unit to new story (by Engagement Unit ID)
 * @returns {`/engagementUnit/${string}/engagementUnitToNewStory`}
 */
export const addEngagementUnitToNewStoryEndpoint = (
  engagementUnitId,
  copiedFromStoryId
) =>
  `/engagementUnit/${engagementUnitId}/engagementUnitToNewStory/${copiedFromStoryId}`;

/**
 * POST Add Engagement Unit to existing story (by Engagement Unit ID)
 * @returns {`/engagementUnit/${string}/engagementUnitToExistingStory/${string}`}
 */
export const addEngagementUnitToExistingStoryEndpoint = engagementUnitId =>
  `/engagementUnit/${engagementUnitId}/engagementUnitToExistingStory`;

/** ################################ **
 *  ##### CATEGORIES ENDPOINTS #####
 *  ################################ */

/**
 * GET All Categories
 * @returns {`/category`}
 */
export const categoriesEndpoint = '/category';

/**
 * GET All Category Type Definitions
 * @returns {`/categoryTypeDefinition`}
 */
export const categoryTypeDefinitionsEndpoint = '/categoryTypeDefinition';

/**
 * GET/PUT/DELETE Category Type Definition
 * @returns {`/categoryTypeDefinition/{id}`}
 */
export const categoryTypeDefinitionEndpoint = categoryTypeDefinitionId =>
  `/categoryTypeDefinition/${categoryTypeDefinitionId}`;

/**
 * PUT Move Category Type Definition
 * @returns {`/categoryTypeDefinition/{id}/move`}
 */
export const categoryTypeDefinitionMoveEndpoint = categoryTypeDefinitionId =>
  `/categoryTypeDefinition/${categoryTypeDefinitionId}/move`;

/**
 * GET Categories with contextual suggestions
 * @returns {`/category/with-suggestions`}
 */
export const categoriesWithSuggestionsEndpoint = '/category/with-suggestions';

/**
 * POST Category to Story
 * @returns {`/category/{id}/categoryToStory`}
 */
export const saveCategoryToStoryEndpoint = storyId =>
  `/category/${storyId}/categoryToStory`;

/**
 * POST Remove Category From Story
 * @returns {`/category/{id}/categoryToStory`}
 */
export const removeCategoryFromStoryEndpoint = (storyId, categoryId) =>
  `/category/${storyId}/removeCategoryFromStory/${categoryId}`;

/**
 * POST Category to Interstitial Ad
 * @returns {`/category/{id}/categoryToInterstitialAd`}
 */
export const saveCategoryToInterstitialAdEndpoint = interstitialAdId =>
  `/category/${interstitialAdId}/categoryToInterstitialAd`;

/**
 * POST Remove Category From Interstitial Ad
 * @returns {`/category/{id}/categoryToInterstitialAd`}
 */
export const removeCategoryFromInterstitialAdEndpoint = (
  interstitialAdId,
  categoryId
) =>
  `/category/${interstitialAdId}/removeCategoryFromInterstitialAd/${categoryId}`;

/**
 * GET Category
 * @returns {`category/{string}`}
 */
export const getCategoryEndpoint = categoryId => `/category/${categoryId}`;

/**
 * GET Checks if name is available
 * @returns {`category/nameIsAvailable`}
 */
export const categoryNameAvailableEndpoint = `/category/nameIsAvailable`;

/**
 * GET Checks if externalId is available
 * @returns {`category/externalIdIsAvailable/{externalId}`}
 */
export const categoryExternalIdAvailableEndpoint = externalId =>
  `/category/externalIdIsAvailable/${externalId}`;

/**
 * GET Category
 * @returns {`category/usedCategories`}
 */
export const getUsedCategoriesEndpoint = `/category/usedCategories`;

/**
 * GET Available Custom CDN Urls
 * @returns {`category/availableCustomCdnUrls`}
 */
export const availableCustomCdnUrlsEndpoint = `/category/availableCustomCdnUrls`;

/**
 * GET Category Types for select fields
 * @returns {`categoryTypeDefinition/categoryTypesForSelect`}
 */
export const categoryTypesForSelectEndpoint = `/categoryTypeDefinition/categoryTypesForSelect`;

/**
 * GET/POST CMS Placements
 * @returns {`/cms-placements`}
 */
export const cmsPlacementsEndpoint = '/cms-placements';

/**
 * GET Checks if CMS Placement code is available
 * @returns {`/cms-placements/codeIsAvailable`}
 */
export const cmsPlacementCodeAvailableEndpoint =
  '/cms-placements/codeIsAvailable';

/**
 * GET/PUT/DELETE CMS Placement
 * @returns {`/cms-placements/{id}`}
 */
export const cmsPlacementEndpoint = placementId =>
  `/cms-placements/${placementId}`;

/**
 * GET CMS Placement delete preview
 * @returns {`/cms-placements/{id}/delete-preview`}
 */
export const cmsPlacementDeletePreviewEndpoint = placementId =>
  `/cms-placements/${placementId}/delete-preview`;

/**
 * GET CMS Placements for category assignment
 * @returns {`/cms-placements/for-select`}
 */
export const cmsPlacementsForSelectEndpoint = '/cms-placements/for-select';

/**
 * DELETE Category
 * @returns {`category/{string}`}
 */
export const deleteCategoryEndpoint = categoryId => `/category/${categoryId}`;

/**
 * POST Starts a Category import from a CSV already uploaded to tenant storage.
 * Body: `{ objectKey }` from the upload. The result arrives over SignalR.
 * @returns {`/category/import-csv`}
 */
export const importCategoriesFromCsvEndpoint = '/category/import-csv';

/**
 * GET Category canSafelyDelete information
 * @returns {`/category/{string}/canSafelyDelete`}
 */
export const canSafelyDeleteCategoryEndpoint = categoryId =>
  `/category/${categoryId}/canSafelyDelete`;

/** ####################################### **
 *  ##### SEARCH ALGORITHMS ENDPOINTS #####
 *  ####################################### */

/**
 * GET/POST Search Algorithms
 * @returns {`/searchAlgorithms`}
 */
export const searchAlgorithmsEndpoint = '/searchAlgorithms';

/**
 * GET/PUT/DELETE Search Algorithm by Id
 * @returns {`/searchAlgorithms/{string}`}
 */
export const searchAlgorithmByIdEndpoint = searchAlgorithmId =>
  `/searchAlgorithms/${searchAlgorithmId}`;

/**
 * POST Search Algorithm Duplicate
 * @returns {`/searchAlgorithms/{string}/duplicate`}
 */
export const searchAlgorithmDuplicateEndpoint = searchAlgorithmId =>
  `/searchAlgorithms/${searchAlgorithmId}/duplicate`;

/**
 * GET/POST Search Algorithm Category Boosts
 * @returns {`/searchAlgorithms/{string}/categoryBoosts`}
 */
export const searchAlgorithmCategoryBoostsEndpoint = searchAlgorithmId =>
  `/searchAlgorithms/${searchAlgorithmId}/categoryBoosts`;

/**
 * PUT/DELETE Search Algorithm Category Boost by Id
 * @returns {`/searchAlgorithms/{string}/categoryBoosts/{string}`}
 */
export const searchAlgorithmCategoryBoostByIdEndpoint = (
  searchAlgorithmId,
  categoryBoostId
) => `/searchAlgorithms/${searchAlgorithmId}/categoryBoosts/${categoryBoostId}`;

/**
 * GET/POST Search Algorithm Personalizations
 * @returns {`/searchAlgorithms/{string}/personalizations`}
 */
export const searchAlgorithmPersonalizationsEndpoint = searchAlgorithmId =>
  `/searchAlgorithms/${searchAlgorithmId}/personalizations`;

/**
 * PUT/DELETE Search Algorithm Personalization by Id
 * @returns {`/searchAlgorithms/{string}/personalizations/{string}`}
 */
export const searchAlgorithmPersonalizationByIdEndpoint = (
  searchAlgorithmId,
  personalizationId
) =>
  `/searchAlgorithms/${searchAlgorithmId}/personalizations/${personalizationId}`;

/**
 * POST Search Algorithm Preview
 * @returns {`/searchAlgorithms/{string}/preview`}
 */
export const searchAlgorithmPreviewEndpoint = searchAlgorithmId =>
  `/searchAlgorithms/${searchAlgorithmId}/preview`;

/**
 * GET Category forTenant
 * @returns {`/category/forTenant/{tenantId}`}
 */
export const forTenantCategoryEndpoint = tenantId =>
  `/category/forTenant/${tenantId}`;

/** ########################## **
 *  ##### APPS ENDPOINTS #####
 *  ########################## */

/**
 * GET Returns All Apps
 * @returns {`/apps`}
 */
export const appsEndpoint = `/apps`;

/**
 * GET/PUT/DELETE App
 * @returns {`/apps/${string}`}
 */
export const appByIdEndpoint = appId => `/apps/${appId}`;

/**
 * GET Returns an App key's analytics events in the last 24 hours (ST-5587)
 * @returns {`/apps/${string}/usage`}
 */
export const appUsageEndpoint = appId => `/apps/${appId}/usage`;

/**
 * GET Polls
 * @returns {`/poll/forEngagementUnitTable`}
 */
export const pollsEndpoint = '/poll/forEngagementUnitTable';

/**
 * GET TriviaQuizzes
 * @returns {`/triviaQuiz`}
 */
export const triviaQuizzesEndpoint = '/triviaQuiz';

/**
 * GET/DELETE TriviaQuiz
 * @returns {`/triviaQuiz/${string}`}
 */
export const triviaQuizEndpoint = triviaQuizId => `/triviaQuiz/${triviaQuizId}`;

/**
 * GET TriviaQuiz Stats
 * @returns {`/triviaQuiz/${string}/stats`}
 */
export const getTriviaQuizStatsEndpoint = triviaQuizId =>
  `/triviaQuiz/${triviaQuizId}/stats`;

/**
 * GET TriviaQuiz ByQuestion Stats
 * @returns {`/triviaQuiz/${string}/byQuestionStats`}
 */
export const getTriviaQuizByQuestionStatsEndpoint = triviaQuizId =>
  `/triviaQuiz/${triviaQuizId}/byQuestionStats`;

/**
 * GET/DELETE Poll
 * @returns {`/poll/${string}`}
 */
export const pollEndpoint = pollId => `/poll/${pollId}`;

/**
 * GET Poll Stats
 * @returns {`/poll/${string}/stats`}
 */
export const getPollStatsEndpoint = pollId => `/poll/${pollId}/stats`;

/** ##################################### **
 *  ########## USER ENDPOINTS ###########
 *  ##################################### */

/**
 * GET/POST User
 * @returns {`/user`}
 */
export const userEndpoint = `/user`;

/**
 * GET/PUT/DELETE User by id
 * @returns {`/user/${string}`}
 */
export const userByIdEndpoint = userId => `/user/${userId}`;

/**
 * POST Checks a tracked user invitation link
 * @returns {`/user/invitation-status`}
 */
export const userInvitationStatusEndpoint = `/user/invitation-status`;

/**
 * POST Resends a tracked user invitation
 * @returns {`/user/${string}/resend-invitation`}
 */
export const resendUserInvitationEndpoint = userId =>
  `/user/${userId}/resend-invitation`;

/**
 * GET currently logged in user data
 * @returns {`/user/userInfo`}
 */
export const currentUserEndpoint = `/user/userInfo`;

/**
 * GET Available Roles
 * @returns {`/user/availableRoles`}
 */
export const userAvailableRoles = `/user/availableRoles`;

/** ##################################### **
 *  ########## AUTH ENDPOINTS ###########
 *  ##################################### */

/**
 * GET/POST AuthRefresh
 * @returns {`/auth/refresh`}
 */
export const authTokenRefreshEndpoint = '/auth/refresh';

/**
 * GET/POST User
 * @returns {`/auth/loginMethods`}
 */
export const loginMethodsEndpoint = `/auth/loginMethods`;

/** ############################# **
 *  ##### TENANTS ENDPOINTS #####
 *  ############################# */

/**
 * GET currently logged in tenant data
 * @returns {`/tenant/currentTenant`}
 */
export const currentTenantEndpoint = `/tenant/currentTenant`;

/**
 * UPDATE the cards cache for the current tenant
 * @returns {`/tenant/cards/cache`}
 */
export const updateCardsCacheEndpoint = `/tenant/cards/cache`;

/**
 * UPDATE the content cache for the current tenant
 * @returns {`/tenant/updateContentCacheForTenant`}
 */
export const updateContentCacheForTenantEndpoint = `/tenant/updateContentCacheForTenant`;

/**
 * UPDATE the Clips cache for the current tenant
 * @returns {`/tenant/clips/cache`}
 */
export const updateClipsCacheForTenantEndpoint = `/tenant/clips/cache`;

/**
 * GET tenant list
 * @returns {`/tenant/:id`}
 */
export const tenantByIdEndpoint = id => `/tenant/${id}`;

/**
 * GET tenant list
 * @returns {`/tenant`}
 */
export const tenantEndpoint = `/tenant`;

/**
 * POST Updates all web stories for a tenant
 * @returns {`/tenant/regenerateWebStories/${string}`}
 */
export const updateWebStoriesEndpoint = tenantId =>
  `/tenant/regenerateWebStories/${tenantId}`;

/**
 * GET/POST Tenant Settings
 * @returns {`/tenant/regenerateWebStories/${string}`}
 */
export const settingsForTenantEndpoint = tenantId =>
  `/settings/settingsForTenant/${tenantId}`;

/**
 * GET/PUT Tenant Theme
 * @returns {`/settings/themeForTenant/${string}`}
 */
export const tenantThemeForTenantEndpoint = tenantId =>
  `/settings/themeForTenant/${tenantId}`;

/**
 * GET Login image for the current tenant
 * @returns {'/tenant/loginImage'}
 */
export const getLoginImageEndpoint = () => '/tenant/loginImage';

/** ################################### **
 *  ##### FEATURE FLAGS ENDPOINTS #####
 *  ################################### */

/**
 * GET Returns all of tenant's feature flags
 * @param tenantId
 * @returns {`/feature/${string}`}
 */
export const featureFlagsEndpoint = tenantId => `/feature/${tenantId}`;

/**
 * POST Sets feature flags for all tenants
 * @returns {`/feature/updateForAllTenants`}
 */
export const featureFlagsUpdateForAllTenantsEndpoint = `/feature/updateForAllTenants`;

/** ################################### **
 *  ##### STORIES ENDPOINTS #####
 *  ################################### */

/**
 * GET All Stories
 * @returns {`/story`}
 */
export const storiesEndpoint = `/story`;

/**
 * GET All Stories in a slim dto
 * @returns {`/story/slimList`}
 */
export const slimStoriesEndpoint = `/story/slimList`;

/**
 * GET All Stories in a slim dto
 * @returns {`/story/v2/slimList`}
 */
export const slimStoriesEndpointV2 = `/story/v2/slimList`;

/**
 * GET / PUT / DELETE Story
 * @returns {`story/{string}`}
 */
export const storyEndpoint = storyId => `/story/${storyId}`;

/**
 * PUT story external ID
 * @returns {`/story/${string}/externalId`}
 */
export const storyExternalIdEndpoint = storyId =>
  `/story/${storyId}/externalId`;

/**
 * POST story
 * @returns {`/story`}
 */
export const postNewStoryEndpoint = `/story`;

/**
 * GET Story Stats
 * @returns {`story/{string}/stats`}
 */
export const getStatsEndpoint = storyId => `/story/${storyId}/stats`;

/**
 * GET Story's Polls Stats
 * @returns {`story/{string}/pollStats`}
 */
export const getStoryPollsStatsEndpoint = storyId =>
  `/story/${storyId}/pollStats`;

/**
 * GET Story's Trivia Quizzes Stats
 * @returns {`story/{string}/triviaQuizStats`}
 */
export const getStoryTriviaQuizStatsEndpoint = storyId =>
  `/story/${storyId}/triviaQuizStats`;

/**
 * GET ByPage Stats
 * @returns {`story/{string}/byPageStats`}
 */
export const getByPageStatsEndpoint = storyId =>
  `/story/${storyId}/byPageStats`;

/**
 * GET/PUT story live status.
 * @param id - live story ID
 * @returns {`/story/${string}/liveStatus`}
 */
export const liveStoryStatusEndpoint = id => `/story/${id}/liveStatus`;

/**
 * PUT story custom chip settings.
 * @param id - story ID
 * @returns {`/story/${string}/customChip`}
 */
export const customChipEndpoint = id => `/story/${id}/customChip`;

/**
 * POST story pinned status.
 * @param id - pinned story ID
 * @returns {`/story/${string}/togglePinned`}
 */
export const pinnedStoryStatusEndpoint = id => `/story/${id}/togglePinned`;

/**
 * PUT story title
 * @param id - page ID
 * @returns {`/story/${string}/title`}
 */
export const storyTitleEndpoint = id => `/story/${id}/title`;

/**
 * POST public preview status
 * @param id - story ID
 * @returns {`/story/${string}/togglePreviewIsPublic`}
 */
export const storyTogglePreviewIsPublicEndpoint = id =>
  `/story/${id}/togglePreviewIsPublic`;

/**
 * PUT page order
 * @param id - page ID
 * @returns {`/story/${string}/pageOrder`}
 */
export const pageOrderEndpoint = id => `/story/${id}/pageOrder`;

/**
 * PUT Schedule Story
 * @param id - story ID
 * @returns {`/story/${string}/schedule`}
 */
export const scheduleStoryEndpoint = id => `/story/${id}/schedule`;

/**
 * POST Unschedule Story
 * @param id - story ID
 * @returns {`/story/${string}/unschedule`}
 */
export const unscheduleStoryStoryEditorEndpoint = id =>
  `/story/${id}/unschedule`;
/**
 * POST new pages
 * @param id - page ID
 * @returns {`/story/newPages/${string}`}
 */
export const storyNewPagesEndpoint = id => `/story/newPages/${id}`;

/**
 * PUT Add Custom Round Image
 * @param id - Story ID
 * @returns {`/story/${string}/roundImage`}
 */
export const addRoundImageEndpoint = id => `/story/${id}/roundImage`;

/**
 * PUT Add Custom Thumbnail Image
 * @param id - Story ID
 * @returns {`/story/${string}/thumbnail`}
 */
export const addThumbnailEndpoint = id => `/story/${id}/thumbnail`;

/**
 * POST Reset Asset to Default
 * @param id - Story ID
 * @returns {`/story/${string}/resetDefault`}
 */
export const resetAssetEndpoint = id => `/story/${id}/resetDefault`;

/**
 * POST Download Story Assets as zip
 * @param id - page ID
 * @returns {`/story/${string}/downloadStoryAsZip`}
 */
export const downloadStoryAsZipEndpoint = id =>
  `/story/${id}/downloadStoryAsZip`;

/**
 * GET Story Preview
 * @param id - page ID
 * @returns {`/story/${string}/preview`}
 */
export const storyPreviewEndpoint = id => `/story/${id}/preview`;

/**
 * GET Story Public Preview
 * @param id - page ID
 * @returns {`/story/${string}/publicPreview`}
 */
export const storyPublicPreviewEndpoint = id => `/story/${id}/publicPreview`;

/**
 * GET / POST Story Audience Targeting Key/Value Pairs
 * @param id - story ID
 * @returns {`/story/${string}/audience-targeting`}
 */
export const getStoryAudienceTargetingEndpoint = id =>
  `/story/${id}/audience-targeting`;

/**
 * PUT / DELETE Story Audience Targeting Key/Value Pairs
 * @param id - story ID
 * @param audienceTargetingId - story ID
 * @returns {`/story/${string}/audience-targeting/${string}`}
 */
export const editStoryAudienceTargetingEndpoint = (id, audienceTargetingId) =>
  `/story/${id}/audience-targeting/${audienceTargetingId}`;

/**
 * PUT Update Story AMPHTML
 * @param id - story ID
 * @returns {`/story/${string}/storyAmpHtml`}
 */
export const updateStoryAmpHtmlEndpoint = id => `/story/${id}/storyAmpHtml`;

/**
 * GET All Audience Targeting Attributes
 * @returns {`/audience-targeting/attribute`}
 */
export const getAudienceTargetingAttributesEndpoint = `/audience-targeting/attribute`;

/** ######################################### **
 *  ##### AUDIENCE MANAGEMENT ENDPOINTS #####
 *  ######################################### */

/**
 * GET/POST Audience Management keys
 * @returns {`/audienceManagement/keys`}
 */
export const audienceKeysEndpoint = '/audienceManagement/keys';

/**
 * GET/DELETE an Audience Management key
 * @returns {`/audienceManagement/keys/${string}`}
 */
export const audienceKeyEndpoint = keyId => `${audienceKeysEndpoint}/${keyId}`;

/**
 * PUT an Audience Management key display name
 * @returns {`/audienceManagement/keys/${string}/displayName`}
 */
export const audienceKeyDisplayNameEndpoint = keyId =>
  `${audienceKeyEndpoint(keyId)}/displayName`;

/**
 * GET Audience Management key usage
 * @returns {`/audienceManagement/keys/${string}/usage`}
 */
export const audienceKeyUsageEndpoint = keyId =>
  `${audienceKeyEndpoint(keyId)}/usage`;

/**
 * GET/POST Audience Management values for a key
 * @returns {`/audienceManagement/keys/${string}/values`}
 */
export const audienceValuesEndpoint = keyId =>
  `${audienceKeyEndpoint(keyId)}/values`;

/**
 * DELETE an Audience Management value
 * @returns {`/audienceManagement/keys/${string}/values/${string}`}
 */
export const audienceValueEndpoint = (keyId, valueId) =>
  `${audienceValuesEndpoint(keyId)}/${valueId}`;

/**
 * PUT an Audience Management value display name
 * @returns {`/audienceManagement/keys/${string}/values/${string}/displayName`}
 */
export const audienceValueDisplayNameEndpoint = (keyId, valueId) =>
  `${audienceValueEndpoint(keyId, valueId)}/displayName`;

/**
 * GET Audience Management value usage
 * @returns {`/audienceManagement/keys/${string}/values/${string}/usage`}
 */
export const audienceValueUsageEndpoint = (keyId, valueId) =>
  `${audienceValueEndpoint(keyId, valueId)}/usage`;

/**
 * POST Story Metadata
 * @returns {`/story/${string}/metadata`}
 */
export const saveStoryMetadata = storyId => `/story/${storyId}/metadata`;

/**
 * POST Duplicate Story
 * @returns {`/story/${string}/duplicate`}
 */
export const duplicateStoryEndpoint = storyId => `/story/${storyId}/duplicate`;

/** ################################### **
 *  ##### PAGES ENDPOINTS #####
 *  ################################### */

/**
 * POST page reupload
 * @param id - page ID
 * @returns {`/page/${string}/reUpload`}
 */
export const pageReuploadEndpoint = id => `/page/${id}/reUpload`;

/**
 * GET/PUT/DELETE page
 * @param id - page ID
 * @returns {`/page/${string}`}
 */
export const pageEndpoint = id => `/page/${id}`;

/**
 * POST toggle page hidden status
 * @param id - page ID
 * @returns {`/page/${string}/toggleHidden`}
 */
export const pageToggleHiddenEndpoint = id => `/page/${id}/toggleHidden`;

/**
 * POST bulk set pages hidden status
 * @param storyId - story ID
 * @returns {`/story/${string}/pages/hidden`}
 */
export const bulkSetPagesHiddenEndpoint = storyId =>
  `/story/${storyId}/pages/hidden`;

/**
 * POST bulk apply action to pages
 * @param storyId - story ID
 * @returns {`/story/${string}/pages/action`}
 */
export const bulkApplyActionToPagesEndpoint = storyId =>
  `/story/${storyId}/pages/action`;

/**
 * DELETE bulk delete pages
 * @param storyId - story ID
 * @returns {`/story/${string}/pages`}
 */
export const bulkDeletePagesEndpoint = storyId => `/story/${storyId}/pages`;

/**
 * GET Checks if page asset is processed without error
 * @param pageId
 * @returns {`/page/${string}/hasError`}
 */
export const pageHasErrorEndpoint = pageId => `/page/${pageId}/hasError`;

/**
 * POST Duplicate page to new story
 * @returns {`/page/${string}/duplicateToNewStory`}
 */
export const duplicatePageToNewStoryEndpoint = pageId =>
  `/page/${pageId}/duplicateToNewStory`;

/**
 * POST Duplicate page to existing story
 * @returns {`/page/${string}/duplicateToExistingStory/${string}`}
 */
export const duplicatePageToExistingStoryEndpoint = pageId =>
  `/page/${pageId}/duplicateToExistingStory`;

/**
 * POST Bulk duplicate pages to new story
 * @param storyId - story ID
 * @returns {`/story/${string}/pages/duplicateToNewStory`}
 */
export const bulkDuplicatePagesToNewStoryEndpoint = storyId =>
  `/story/${storyId}/pages/duplicateToNewStory`;

/**
 * POST Bulk duplicate pages to existing story/stories
 * @param storyId - story ID
 * @returns {`/story/${string}/pages/duplicateToExistingStory`}
 */
export const bulkDuplicatePagesToExistingStoryEndpoint = storyId =>
  `/story/${storyId}/pages/duplicateToExistingStory`;

/**
 * POST / GET page audience targeting
 * @returns {`/page/${string}/audience-targeting`}
 */
export const pageAudienceTargetingEndpoint = pageId =>
  `/page/${pageId}/audience-targeting`;

/**
 * PUT / DELETE page audience targeting
 * @returns {`/page/${string}/audience-targeting/${string}`}
 */
export const pageAudienceTargetingByIdEndpoint = (pageId, id) =>
  `/page/${pageId}/audience-targeting/${id}`;

/** ################################### **
 *  ##### CLIPS ENDPOINTS #####
 *  ################################### */

/**
 * GET/POST Clip
 * @returns {`/clips`}
 */
export const clipsEndpoint = `/clips`;

/**
 * GET/PUT Clip
 * @returns {`/clips/{string}`}
 */
export const singleClipEndpoint = clipId => `/clips/${clipId}`;

/**
 * DELETE Clip
 * @returns {`/clips/${string}`}
 */
export const deleteClipEndpoint = clipId => `/clips/${clipId}`;

/**
 * Retry Clip Video Processing
 * @returns {`/clips/${string}/retryVideoProcessing`}
 */
export const retryClipVideoProcessingEndpoint = clipId =>
  `/clips/${clipId}/retryVideoProcessing`;

/**
 * POST Reset Custom Thumbnail
 * @param id - Clip ID
 * @returns {`/clips/${string}/resetCustomThumbnail`}
 */
export const resetCustomThumbnailEndpoint = id =>
  `/clips/${id}/resetCustomThumbnail`;

/** ################################### **
 *  ###### COLLECTIONS ENDPOINTS ######
 *  ################################### */

/**
 * GET/POST Collections
 * @returns {`/collections`}
 */
export const collectionsEndpoint = `/collections`;

/**
 * GET/PUT/DELETE Collection
 * @returns {`/collections/:collectionId`}
 */
export const collectionEndpoint = collectionId =>
  `/collections/${collectionId}`;

/**
 * GET Collection Specific Clips
 * @returns {`/collections/:collectionId/collection`}
 */
export const collectionSpecificClipsEndpoint = collectionId =>
  `/collections/${collectionId}/collection`;

/**
 * GET Collection Preview Clips (non-persistent)
 * @returns {`/collections/:collectionId/clips/preview`}
 */
export const collectionPreviewEndpoint = collectionId =>
  `/collections/${collectionId}/clips/preview`;

/**
 * GET Collection Details
 * @returns {`/collections/:collectionId/details`}
 */
export const collectionDetailsEndpoint = collectionId =>
  `/collections/${collectionId}/details`;

/**
 * PUT (with languageCode) / DELETE (without) a Collection's Clip language
 * @returns {`/collections/:collectionId/clip-language[/:languageCode]`}
 */
export const collectionClipLanguageEndpoint = (collectionId, languageCode) =>
  languageCode
    ? `/collections/${collectionId}/clip-language/${encodeURIComponent(
        languageCode
      )}`
    : `/collections/${collectionId}/clip-language`;

/**
 * GET Collection Clips Analytics Dashboard
 * @returns {`/collections/:collectionId/analytics/clips/dashboard`}
 */
export const collectionAnalyticsClipsDashboardEndpoint = collectionId =>
  `/collections/${collectionId}/analytics/clips/dashboard`;

/**
 * PUT OrderingOption for Collection
 * @returns {`/collections/:collectionId/orderingOption`}
 */
export const collectionOrderingOptionEndpoint = collectionId =>
  `/collections/${collectionId}/orderingOption`;

/**
 * PUT Categories for Collection
 * @returns {`/collections/:collectionId/categories`}
 */
export const collectionCategoriesEndpoint = collectionId =>
  `/collections/${collectionId}/categories`;

/**
 * DELETE Single Collection Clip
 * @returns {`/collections/:collectionId/clip/:clipId`}
 */
export const removeSingleCollectionClipEndpoint = (collectionId, clipId) =>
  `/collections/${collectionId}/clip/${clipId}`;

/**
 * PUT Multiple Collection Clips
 * @returns {`/collections/:collectionId/addAndRemoveClips`}
 */
export const addAndRemoveCollectionClipsEndpoint = collectionId =>
  `/collections/${collectionId}/addAndRemoveClips`;

/**
 * PUT Reorder Collection Clips
 * @returns {`/collections/:collectionId/reorderClips`}
 */
export const reorderCollectionClipsEndpoint = collectionId =>
  `/collections/${collectionId}/reorderClips`;

/**
 * PUT Viewed Ordering for Collection
 * @returns {`/collections/:collectionId/viewedOrdering`}
 */
export const collectionViewedOrderingEndpoint = collectionId =>
  `/collections/${collectionId}/viewedOrdering`;

/**
 * PUT Collection Search Algorithm
 * @returns {`/collections/:collectionId/searchAlgorithm/:searchAlgorithmId`}
 */
export const collectionSearchAlgorithmEndpoint = (
  collectionId,
  searchAlgorithmId
) => `/collections/${collectionId}/searchAlgorithm/${searchAlgorithmId}`;

/**
 * DELETE Collection Search Algorithm
 * @returns {`/collections/:collectionId/searchAlgorithm`}
 */
export const collectionSearchAlgorithmClearEndpoint = collectionId =>
  `/collections/${collectionId}/searchAlgorithm`;

/**
 * POST Collection to Interstitial Ad
 * @returns {`/collections/{id}/collectionToInterstitialAd`}
 */
export const saveCollectionToInterstitialAdEndpoint = interstitialAdId =>
  `/collections/${interstitialAdId}/collectionToInterstitialAd`;

/**
 * POST Remove Collection From Interstitial Ad
 * @returns {`/collections/{id}/collectionToInterstitialAd`}
 */
export const removeCollectionFromInterstitialAdEndpoint = (
  interstitialAdId,
  collectionId
) =>
  `/collections/${interstitialAdId}/removeCollectionFromInterstitialAd/${collectionId}`;

/** ##################################### **
 *  ##### INTERSTITIAL AD ENDPOINTS #####
 *  ##################################### */

/**
 * GET All Interstitial Ads
 * @returns {`/interstitialAd`}
 */
export const getAllInterstitialAdEndpoint = '/interstitialAd';

/**
 * GET/DELETE Interstitial Ad
 * @returns {`/interstitialAd/${string}`}
 */
export const interstitialAdEndpoint = adId => `/interstitialAd/${adId}`;

/**
 * GET Interstitial Ad Stats
 * @returns {`/interstitialAd/${string}/stats`}
 */
export const interstitialAdStatsEndpoint = adId =>
  `/interstitialAd/${adId}/stats`;

/**
 * GET Ad IDs for date range
 */
export const getAdIdsForDateRange = () => `/interstitialAd/adIdsForDateRange`;

/**
 * POST interstitial ad
 * @returns {`/interstitialAd`}
 */
export const postNewInterstitialAdEndpoint = `/interstitialAd`;

/**
 * PUT interstitial ad title
 * @param id - Interstitial Ad ID
 * @returns {`/interstitialAd/${string}/title`}
 */
export const putInterstitialAdTitlesEndpoint = id =>
  `/interstitialAd/${id}/title`;

/**
 * POST Duplicate interstitial ad
 * @returns {`/interstitialAd/${string}/duplicate`}
 */
export const duplicateInterstitialAdEndpoint = id =>
  `/interstitialAd/${id}/duplicate`;

/**
 * PUT interstitial ad publish status.
 * @param id - live interstitial ad ID
 * @returns {`/interstitialAd/${string}/status`}
 */
export const publishInterstitialAdStatusEndpoint = id =>
  `/interstitialAd/${id}/status`;

/**
 * PUT interstitial ad action.
 * @param id - live interstitial ad ID
 * @returns {`/interstitialAd/${string}/action`}
 */
export const putInterstitialAdActionEndpoint = id =>
  `/interstitialAd/${id}/action`;

/**
 * PUT interstitial ad advertiser name
 * @returns {`/interstitialAd/${string}/advertiserName`}
 */
export const putAdvertiserName = id => `/interstitialAd/${id}/advertiserName`;

/**
 * POST Schedule interstitial ad for story
 * @param id - interstitialAd ID
 * @returns {`/interstitialAd/${string}/schedule/category`}
 */
export const postScheduleStoryAdEndpoint = id =>
  `/interstitialAd/${id}/schedule/category`;

/**
 * POST Unschedule interstitial ad for story
 * @param id - interstitialAd ID
 * @returns {`/interstitialAd/${string}/unSchedule/category`}
 */
export const postUnscheduleStoryAdEndpoint = id =>
  `/interstitialAd/${id}/unSchedule/category`;

/**
 * POST Schedule interstitial ad for clip
 * @param id - interstitialAd ID
 * @returns {`/interstitialAd/${string}/schedule/collection`}
 */
export const scheduleClipAdEndpoint = id =>
  `/interstitialAd/${id}/schedule/collection`;

/**
 * POST Unschedule interstitial ad for clip
 * @param id - interstitialAd ID
 * @returns {`/interstitialAd/${string}/unSchedule/collection`}
 */
export const unscheduleClipAdEndpoint = id =>
  `/interstitialAd/${id}/unSchedule/collection`;

/** ##################################### **
 *  ##### SCHEDULE ENDPOINTS #####
 *  ##################################### */

export const categoryScheduleEndpoint = `/schedule`;

export const categoryOrderingOptionEndpoint = `/schedule/ordering`;

export const reorderScheduleStoriesEndpoint = `/schedule/reorderStories`;

export const unscheduleStoryEndpoint = storyId =>
  `/schedule/unscheduleStory/${storyId}`;

/**
 * PUT story publish status.
 * @param id - live story ID
 * @returns {`/story/${string}/publishStatus`}
 */
export const publishStoryStatusEndpoint = id => `/story/${id}/publishStatus`;

/** ##################################### **
 *  ##### INSTAGRAM ENDPOINTS #####
 *  ##################################### */

/**
 * GET All Instagram Accounts With PAges
 * @returns {`/instagramAccounts/accountsWithPages`}
 */
export const instagramAccountsWithPagesEndpoint =
  '/instagramAccounts/accountsWithPages';

/**
 * GET All Instagram's Account Pages
 * @returns {`/instagramPages`}
 */
export const instagramPagesEndpoint = '/instagramPages';

/**
 * GET / POST All Instagram Accounts
 * @returns {`/instagramAccounts`}
 */
export const instagramAccountsEndpoint = '/instagramAccounts';

/**
 * GET / PUT / DELETE Instagram Account
 * @param id - Instagram Account ID
 * @returns {`/instagramAccounts/${id}`}
 */
export const instagramAccountEndpoint = id => `/instagramAccounts/${id}`;

/**
 * POST Schedule Import Stories
 * @param instagramAccountId - Instagram Account ID
 * @returns {`/instagramAccounts/scheduleImportStories/${instagramAccountId}`}
 */
export const scheduleImportStoriesEndpoint = instagramAccountId =>
  `/instagramAccounts/scheduleImportStories/${instagramAccountId}`;

/**
 * GET Instagram Business Accounts
 * @returns {`/instagramAccounts/instagramBusinessAccounts`}
 */
export const instagramBusinessAccountsEndpoint = `/instagramAccounts/instagramBusinessAccounts`;

/**
 * POST Instagram Accounts
 * @returns {`/instagramAccounts/saveAccounts`}
 */
export const saveInstagramAccountsEndpoint = `/instagramAccounts/saveAccounts`;

/**
 * POST Instagram Pages To Story
 * @returns {`/story/${id}/instagramPages`}
 */
export const postInstagramPagesToStoryEndpoint = id =>
  `/story/${id}/instagramPages`;

/** ############################# **
 *  ##### ANALYTICS ENDPOINTS #####
 *  ############################# */

/**
 * GET Analytics platforms
 */
export const analyticsPlatformsEndpoint = `/analytics/platforms`;

export const analyticsCategoriesEndpoint = `/analytics/lookups/categories`;

export const analyticsCategoryEndpoint = categoryId =>
  `/analytics/lookups/categories/${categoryId}`;

export const analyticsCategoryResolutionEndpoint = `/analytics/lookups/categories/resolve`;

export const analyticsCollectionsEndpoint = `/analytics/lookups/collections`;

export const analyticsCollectionEndpoint = collectionId =>
  `/analytics/lookups/collections/${collectionId}`;

export const analyticsAudienceAttributesEndpoint = `/analytics/lookups/audienceAttributes`;

export const adsAnalyticsAccessEndpoint = `/analytics/lookups/adsAccess`;

export const analyticsContentContextEndpoint = (resource, id) =>
  `/${resource}/${id}/analyticsContext`;

/**
 * GET Stats
 */
export const statsEndpoint = `/analytics/stats`;

/**
 * GET Stats grouped by Story
 */
export const statsGroupedByStoryEndpoint = `/analytics/groupedByStoryStats`;

/**
 * GET Stats grouped by Platform
 */
export const statsGroupedByPlatformEndpoint = `/analytics/groupedByPlatformStats`;

/**
 * GET Stats grouped by Category
 */
export const statsGroupedByCategoryEndpoint = `/analytics/groupedByCategoryStats`;

/**
 * GET Unique Viewers
 */
export const getUniqueViewers = `/analytics/uniqueViewers`;

/** ############################# **
 *  ## STUDIO ASSETS ENDPOINTS ##
 *  ############################# */

/**
 * GET/POST Studio Assets Text Sets
 * @type {string}
 */
export const textSetsEndpoint = '/studioTextSet';

/**
 * GET Studio Asset Text Sets by ID
 * @param id
 * @returns {`/studioTextSet/${string}`}
 */
export const textSetByIdEndpoint = id => `/studioTextSet/${id}`;

/**
 * GET/POST Studio Assets Text Sets
 * @type {string}
 */
export const templatesEndpoint = '/template';

/**
 * GET Templates folders list
 * @type {string}
 */
export const templatesFolderEndpoint = '/template/templateFolders';

/**
 * GET/POST template folders used by CMS folder navigation and management
 * @type {string}
 */
export const templateFoldersEndpoint = '/template-folders';

/**
 * GET/PUT/DELETE a template folder by ID
 * @param id
 * @returns {`/template-folders/${string}`}
 */
export const templateFolderByIdEndpoint = id => `/template-folders/${id}`;

/**
 * GET all existing folders as template move destinations
 * @type {string}
 */
export const templateFolderDestinationsEndpoint =
  '/template-folders/destinations';

/**
 * GET Studio Asset Text Sets by ID
 * @param id
 * @returns {`/studioTextSet/${string}`}
 */
export const templateByIdEndpoint = id => `/template/${id}`;

/** POST a saved template copy, including its Automation configuration. */
export const duplicateTemplateEndpoint = id => `/template/${id}/duplicate`;

/**
 * GET/POST Template Properties
 * @param templateId
 * @returns {`/template-properties/${string}`}
 */
export const templatePropertiesEndpoint = templateId =>
  `/template-properties/${templateId}`;

/**
 * PUT/DELETE Template Property
 * @param templateId
 * @param propertyId
 * @returns {`/template-properties/${string}/${string}`}
 */
export const templatePropertyByIdEndpoint = (templateId, propertyId) =>
  `/template-properties/${templateId}/${propertyId}`;

/**
 * GET/POST Studio Animations
 * @type {string}
 */
export const studioAnimationsEndpoint = '/studioAnimation';

/**
 * GET/PUT/DELETE Studio Animation by ID
 * @param id
 * @returns {`/studioAnimation/${string}`}
 */
export const studioAnimationByIdEndpoint = id => `/studioAnimation/${id}`;

/**
 * GET Studio LUT filters for CMS management and POST a new filter.
 * @type {string}
 */
export const studioLutFiltersEndpoint = '/studio/lut-filters';

/**
 * GET Studio LUT filters including archived filters.
 * @type {string}
 */
export const studioLutFiltersManagementEndpoint =
  '/studio/lut-filters/management';

/**
 * GET/PUT Studio LUT filter by ID.
 * @param id
 * @returns {`/studio/lut-filters/${string}`}
 */
export const studioLutFilterByIdEndpoint = id => `/studio/lut-filters/${id}`;

/**
 * POST a replacement version for a Studio LUT filter.
 * @param id
 * @returns {`/studio/lut-filters/${string}/versions`}
 */
export const studioLutFilterVersionsEndpoint = id =>
  `/studio/lut-filters/${id}/versions`;

/**
 * POST an archive or unarchive action for a Studio LUT filter.
 * @param id
 * @param action
 * @returns {`/studio/lut-filters/${string}/${string}`}
 */
export const studioLutFilterStatusEndpoint = (id, action) =>
  `/studio/lut-filters/${id}/${action}`;

/**
 * GET Shapes list
 * @type {string}
 */
export const studioShapeEndpoint = '/studioShape';

/**
 * GET Shape by ID
 * @param id
 * @returns {`/studioShape/${string}`}
 */
export const studioShapeByIdEndpoint = id => `/studioShape/${id}`;

/**
 * GET/POST Font
 * @type {string}
 */
export const studioFontEndpoint = `/studioFont`;

/**
 * GET Font by ID
 * @param id
 * @returns {`/studioFont/${string}`}
 */

export const studioFontByIdEndpoint = id => `/studioFont/${id}`;
/* GET Stickers list
 * @type {string}
 */
export const studioStickerEndpoint = '/studioSticker';

/**
 * GET Sticker by ID
 * @param id
 * @returns {`/studioSticker/${string}`}
 */
export const studioStickerByIdEndpoint = id => `/studioSticker/${id}`;

/**
 * GET/POST Studio Saved Colors
 * @type {string}
 */
export const studioSavedColorsEndpoint = '/studioSavedColors';

/**
 * GET/PATCH/DELETE Studio Saved Color by ID
 * @param id
 * @returns {`/studioSavedColors/${string}`}
 */
export const studioSavedColorByIdEndpoint = id => `/studioSavedColors/${id}`;

/**
 * GET Media Source List
 * @returns {`/mediaSource/GetList`}
 */
export const mediaSourceGetListEndpoint = '/mediaSource/getList';

/**
 * GET/POST editor-safe CMS Media Libraries
 * @type {string}
 */
export const cmsMediaLibrariesEndpoint = '/cmsMediaLibrary/libraries';

/**
 * GET/PUT editor-safe CMS Media Library
 * @returns {`/cmsMediaLibrary/libraries/${string}`}
 */
export const cmsMediaLibraryByIdEndpoint = id =>
  `/cmsMediaLibrary/libraries/${id}`;

/**
 * GET / DELETE Media Source
 * @returns {`/mediaSource/${string}`}
 */
export const mediaSourceEndpointById = id => `/mediaSource/${id}`;

/**
 * GET Media Source Settings
 * @returns {`/mediaSource/${string}/settings`}
 */
export const mediaSourceSettingsEndpointById = id =>
  `/mediaSource/${id}/settings`;

/**
 * GET Media Source provider Login URL
 * @returns {`/mediaSource/${string}/authorize-url`}
 */
export const mediaSourceAuthorizeUrlEndpointById = id =>
  `/mediaSource/${id}/authorize-url`;

/**
 * POST / PUT Media Source
 * @returns {`/mediaSource`}
 */
export const mediaSourceEndpoint = '/mediaSource';

/**
 * POST / PUT Media Source External Asset
 * @returns {`/mediaSource/externalAsset`}
 */
export const mediaSourceExternalAssetEndpoint = '/mediaSource/externalAsset';

/**
 * GET / DELETE Media Source External Asset
 * @returns {`/mediaSource/externalAsset/${string}`}
 */
export const mediaSourceExternalAssetByIdEndpoint = id =>
  `/mediaSource/externalAsset/${id}`;

/**
 * POST Create Story from Media Source Assets
 * @returns {`/mediaSource/stories`}
 */
export const mediaSourceStoriesEndpoint = '/mediaSource/stories';

/**
 * POST Add Media Source Assets to Story
 * @returns {`/mediaSource/stories/${string}/pages`}
 */
export const mediaSourceStoryPagesEndpoint = storyId =>
  `/mediaSource/stories/${storyId}/pages`;

/**
 * POST Create Clip from Media Source Asset
 * @returns {`/mediaSource/clips`}
 */
export const mediaSourceClipsEndpoint = '/mediaSource/clips';

/**
 * PUT Media Source Change Display Order
 * @returns {`/mediaSource/displayOrder`}
 */
export const mediaSourceChangeDisplayOrderEndpoint =
  '/mediaSource/displayOrder';

/** ############################# **
 *  ## RECIPES ENDPOINTS ##
 *  ############################# */

/**
 * GET Recipes
 * @returns {`/recipes`}
 */
export const recipesEndpoint = '/recipes';

/**
 * GET SingleRecipe
 * @param recipeId
 * @returns {`/recipes/${recipeId}`}
 */
export const getSingleRecipeEndpoint = recipeId => `/recipes/${recipeId}`;

/**
 * GET Recipe Fields
 * @param recipeId
 * @returns {`/recipes/${recipeId}/recipeFields`}
 */
export const getRecipeFieldsEndpoint = recipeId =>
  `/recipes/${recipeId}/recipeFields`;

/**
 * POST Run Recipe
 * @param recipeId
 * @returns {`/recipes/${recipeId}/runRecipe`}
 */
export const runRecipeEndpoint = recipeId => `/recipes/${recipeId}/runRecipe`;

/**
 * GET Recipe Runs
 * @returns {`/recipeRuns`}
 */
export const recipeRunsEndpoint = '/recipeRuns';

/**
 * GET Recipe Run
 * @param runId
 * @returns {`/recipeRuns/${runId}`}
 */
export const getRecipeRunEndpoint = runId => `/recipeRuns/${runId}`;

/** ############################# **
 *  ## RECIPE MANAGEMENT ENDPOINTS ##
 *  ############################# */

/**
 * GET Recipe Management List
 * @param tenantId
 * @returns {`/recipes/${tenantId}/list`}
 */
export const getRecipeManagementListEndpoint = tenantId =>
  `/recipes/${tenantId}/list`;

/**
 * POST Add Recipe
 * @returns {`/recipes`}
 */
export const addRecipeEndpoint = '/recipes';

/**
 * PUT Update Recipe
 * @returns {`/recipes/${recipeId}`}
 */
export const updateRecipeEndpoint = recipeId => `/recipes/${recipeId}`;

/**
 * DELETE Recipe
 * @returns {`/recipes/${recipeId}`}
 */
export const deleteRecipeEndpoint = recipeId => `/recipes/${recipeId}`;

/**
 * GET Available Roles
 * @returns {`/recipes/availableRoles`}
 */

export const availableRolesEndpoint = '/recipes/availableRoles';

/**
 * GET available Recipe Types
 * @returns {`/recipes/recipeTypes`}
 */

export const availableRecipeTypes = '/recipes/recipeTypes';

/** ################################### **
 *  ##### CUSTOM RECIPE ENDPOINTS #####
 *  ################################### */

/**
 * GET NBA Games dates
 * @returns {`/recipes/custom/matchups/nbaGames`}
 */
export const matchupsRecipeNBAGamesEndpoint = (league, teamId) =>
  `/recipes/custom/matchups/nbaGames?league=${league}&teamId=${teamId || ''}`;

/**
 * GET Voiceover content
 * @returns {`/recipes/custom/matchups/voiceoverText`}
 */
export const matchupsRecipeGetVoiceoverTextEndpoint = `/recipes/custom/matchups/voiceoverText`;

/**
 * POST Voiceover audio preview
 * @returns {`/recipes/custom/matchups/voiceoverPreview`}
 */
export const matchupsRecipeGetVoiceoverAudioEndpoint = `/recipes/custom/matchups/voiceoverPreview`;

/**
 * POST NBA Daily Schedule validation
 * @returns {`/recipes/custom/nba/dailySchedule/validate`}
 */
export const nbaDailyScheduleValidateEndpoint =
  '/recipes/custom/nba/dailySchedule/validate';

/**
 * POST Generate Ad Export
 */
export const generateAdExportEndpoint = '/exports/generateAdExport';

/** ########################## **
 *  ###### Studio Story Thumbnail Endpoints #####
 *  ########################## */

/**
 * POST Retry Studio Story Thumbnail Processing
 */
export const retryStudioStoryThumbnailProcessingEndpoint = storyId =>
  `/studioStoryThumbnail/retry-processing/${storyId}`;

/** ########################## **
 *  ###### Studio Story Icon Endpoints #####
 *  ########################## */

/**
 * POST Retry Studio Story Icon Processing
 */
export const retryStudioStoryIconProcessingEndpoint = storyId =>
  `/studioStoryIcon/retry-processing/${storyId}`;

/** ########################## **
 *  ###### Studio Category Profile Background Endpoints #####
 *  ########################## */

/**
 * GET Retry a failed Studio category profile background render
 */
export const retryStudioCategoryProfileBackgroundImageEndpoint = categoryId =>
  `/studioCategoryProfileBackgroundImage/retry-processing/${categoryId}`;
